import { Component, useState, useEffect, useCallback, type ReactNode } from "react";
import { fetchVisitors, clearVisitorsRemote, type VisitorRecord } from "@/hooks/useVisitorTracker";
import { API_ENDPOINTS } from "@/lib/api";
import StatCard from "./components/StatCard";
import CountryChart from "./components/CountryChart";
import VisitorTable from "./components/VisitorTable";
import ContentEditor from "./components/ContentEditor";

const ADMIN_PASSWORD = "8818605";
const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 15 * 60 * 1000; // 15 分钟
const STORAGE_KEY_ATTEMPTS = "admin_login_attempts";
const STORAGE_KEY_LOCK_UNTIL = "admin_lock_until";

interface VerificationChallenge {
  question: string;
  answer: string;
}

interface ContactSubmission {
  id: string;
  name: string;
  phone: string;
  email: string;
  type: string;
  message: string;
  isRead: boolean;
  readAt: string | null;
  createdAt: string | null;
}

function createVerificationChallenge(): VerificationChallenge {
  const left = Math.floor(Math.random() * 8) + 2;
  const right = Math.floor(Math.random() * 8) + 1;
  const useAddition = Math.random() > 0.5;
  if (useAddition) {
    return { question: `${left} + ${right} = ?`, answer: String(left + right) };
  }
  const top = Math.max(left, right);
  const bottom = Math.min(left, right);
  return { question: `${top} - ${bottom} = ?`, answer: String(top - bottom) };
}

class AdminErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="bg-red-950/30 border border-red-800 rounded-xl p-6 text-sm text-red-200">
        <h2 className="text-red-300 font-semibold mb-2 flex items-center gap-2">
          <i className="ri-error-warning-line" />
          内容管理加载失败
        </h2>
        <p className="text-red-200/80 mb-3">后台捕获到运行时错误，页面不会再显示空白。</p>
        <pre className="bg-black/30 border border-red-900 rounded-lg p-3 overflow-auto text-xs whitespace-pre-wrap">{this.state.error.message}</pre>
      </div>
    );
  }
}

function getCountryStats(visitors: VisitorRecord[]) {
  const map: Record<string, { country: string; countryCode: string; count: number }> = {};
  visitors.forEach((v) => {
    const key = v.countryCode;
    if (!map[key]) map[key] = { country: v.country, countryCode: v.countryCode, count: 0 };
    map[key].count += 1;
  });
  const total = visitors.length || 1;
  return Object.values(map)
    .sort((a, b) => b.count - a.count)
    .map((item) => ({ ...item, percent: (item.count / total) * 100 }));
}

function getTodayCount(visitors: VisitorRecord[]): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return visitors.filter((v) => v.timestamp >= today.getTime()).length;
}

function getUniqueIPs(visitors: VisitorRecord[]): number {
  return new Set(visitors.map((v) => v.ip)).size;
}

async function fetchContactSubmissions(): Promise<ContactSubmission[]> {
  const res = await fetch(API_ENDPOINTS.contactList, { headers: { Accept: "application/json" } });
  if (!res.ok) return [];
  const data = await res.json() as { items?: ContactSubmission[] };
  return Array.isArray(data.items) ? data.items : [];
}

async function markSubmissionRead(id: string): Promise<void> {
  await fetch(API_ENDPOINTS.contactMarkRead, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

async function deleteSubmission(id: string): Promise<void> {
  await fetch(API_ENDPOINTS.contactDelete, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
  });
}

function formatSubmissionTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", { hour12: false });
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [verificationInput, setVerificationInput] = useState("");
  const [challenge, setChallenge] = useState<VerificationChallenge>(() => createVerificationChallenge());
  const [pwError, setPwError] = useState(false);
  const [verificationError, setVerificationError] = useState(false);
  const [attempts, setAttempts] = useState<number>(() => {
    const v = localStorage.getItem(STORAGE_KEY_ATTEMPTS);
    return v ? parseInt(v, 10) : 0;
  });
  const [lockUntil, setLockUntil] = useState<number>(() => {
    const v = localStorage.getItem(STORAGE_KEY_LOCK_UNTIL);
    return v ? parseInt(v, 10) : 0;
  });
  const [remaining, setRemaining] = useState<number>(0);
  const [visitors, setVisitors] = useState<VisitorRecord[]>([]);
  const [messages, setMessages] = useState<ContactSubmission[]>([]);
  const [activeTab, setActiveTab] = useState<"overview" | "list" | "content" | "messages">("content");
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [messageBusyId, setMessageBusyId] = useState<string | null>(null);

  const isLocked = Date.now() < lockUntil;

  // 倒计时
  useEffect(() => {
    if (!isLocked) {
      setRemaining(0);
      return;
    }
    const tick = () => {
      const left = Math.max(0, lockUntil - Date.now());
      setRemaining(left);
      if (left === 0) setLockUntil(0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockUntil, isLocked]);

  const loadData = useCallback(async () => {
    const [visitorData, messageData] = await Promise.all([fetchVisitors(), fetchContactSubmissions()]);
    setVisitors(visitorData);
    setMessages(messageData);
  }, []);

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const formatRemaining = (ms: number) => {
    const totalSec = Math.ceil(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleLogin = () => {
    if (isLocked) return;
    const verificationPassed = verificationInput.trim() === challenge.answer;
    if (pwInput === ADMIN_PASSWORD && verificationPassed) {
      setAuthed(true);
      setPwError(false);
      setVerificationError(false);
      const newAttempts = 0;
      setAttempts(newAttempts);
      localStorage.setItem(STORAGE_KEY_ATTEMPTS, String(newAttempts));
      localStorage.removeItem(STORAGE_KEY_LOCK_UNTIL);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      localStorage.setItem(STORAGE_KEY_ATTEMPTS, String(newAttempts));
      setPwError(pwInput !== ADMIN_PASSWORD);
      setVerificationError(!verificationPassed);
      setVerificationInput("");
      setChallenge(createVerificationChallenge());
      if (newAttempts >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCK_DURATION_MS;
        setLockUntil(until);
        localStorage.setItem(STORAGE_KEY_LOCK_UNTIL, String(until));
        setPwError(false);
        setVerificationError(false);
      }
    }
  };

  const handleClear = () => {
    clearVisitorsRemote().then(loadData);
    setShowClearConfirm(false);
  };

  const unreadMessages = messages.filter((item) => !item.isRead).length;

  const handleMarkRead = async (id: string) => {
    setMessageBusyId(id);
    try {
      await markSubmissionRead(id);
      await loadData();
    } finally {
      setMessageBusyId(null);
    }
  };

  const handleDeleteMessage = async (id: string) => {
    if (!window.confirm("确认删除这条留言？此操作不可恢复。")) return;
    setMessageBusyId(id);
    try {
      await deleteSubmission(id);
      await loadData();
    } finally {
      setMessageBusyId(null);
    }
  };

  if (!authed) {
    const locked = Date.now() < lockUntil;
    return (
      <div className="min-h-screen bg-[#070c1a] flex items-center justify-center">
        <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-2xl p-10 w-full max-w-sm">
          <div className="flex items-center justify-center mb-6">
            <div className={`w-14 h-14 flex items-center justify-center rounded-full border text-2xl transition-colors ${
              locked
                ? "bg-red-900/20 border-red-700 text-red-400"
                : "bg-[#0a1628] border-[#1e3a5a] text-[#4fc3f7]"
            }`}>
              <i className={locked ? "ri-lock-password-line" : "ri-lock-2-line"}></i>
            </div>
          </div>
          <h1 className="text-white text-xl font-bold text-center mb-1">后台管理</h1>
          <p className="text-[#5a7a9a] text-sm text-center mb-8">星核动力科技 · 访问统计系统</p>

          {locked ? (
            <div className="space-y-4">
              <div className="bg-red-900/20 border border-red-800 rounded-xl p-5 text-center">
                <p className="text-red-400 font-semibold text-sm mb-1">账户已被临时锁定</p>
                <p className="text-[#5a7a9a] text-xs mb-4">连续输错 {MAX_ATTEMPTS} 次，请稍后再试</p>
                <div className="text-3xl font-mono font-bold text-red-300 tracking-widest">
                  {formatRemaining(remaining)}
                </div>
                <p className="text-[#5a7a9a] text-xs mt-2">剩余锁定时间</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <input
                type="password"
                value={pwInput}
                onChange={(e) => setPwInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                placeholder="请输入管理密码"
                className="w-full bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-4 py-3 text-white text-sm placeholder-[#3a5070] focus:outline-none focus:border-[#4fc3f7] transition-colors"
              />
              <div className="bg-[#0a1628] border border-[#1e2d4a] rounded-lg px-4 py-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-[#8aaec8] text-xs">安全验证</label>
                  <button
                    type="button"
                    onClick={() => { setChallenge(createVerificationChallenge()); setVerificationInput(""); setVerificationError(false); }}
                    className="text-[#5a7a9a] hover:text-[#4fc3f7] text-xs transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-1"></i>
                    刷新题目
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <div className="px-3 py-2 rounded-lg bg-[#071120] border border-[#1e3a5a] text-[#4fc3f7] text-sm font-mono tracking-wide min-w-[96px] text-center">
                    {challenge.question}
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={verificationInput}
                    onChange={(e) => setVerificationInput(e.target.value.replace(/[^0-9-]/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    placeholder="输入答案"
                    className="flex-1 bg-[#071120] border border-[#1e2d4a] rounded-lg px-3 py-2 text-white text-sm placeholder-[#3a5070] focus:outline-none focus:border-[#4fc3f7] transition-colors"
                  />
                </div>
              </div>
              {pwError && (
                <div className="bg-red-900/20 border border-red-800/50 rounded-lg px-3 py-2.5">
                  <p className="text-red-400 text-xs flex items-center gap-1.5">
                    <i className="ri-error-warning-line"></i>
                    密码错误，还剩 <span className="font-bold text-red-300">{MAX_ATTEMPTS - attempts}</span> 次机会
                  </p>
                  {attempts >= 3 && (
                    <p className="text-[#6a8aaa] text-xs mt-1 pl-4">
                      失败 {MAX_ATTEMPTS} 次后账户将锁定 15 分钟
                    </p>
                  )}
                </div>
              )}
              {verificationError && (
                <div className="bg-amber-900/20 border border-amber-800/50 rounded-lg px-3 py-2.5">
                  <p className="text-amber-300 text-xs flex items-center gap-1.5">
                    <i className="ri-shield-keyhole-line"></i>
                    安全验证答案错误，请重新计算后再登录
                  </p>
                </div>
              )}
              <button
                onClick={handleLogin}
                className="w-full bg-[#4fc3f7] hover:bg-[#0288d1] text-[#070c1a] font-semibold rounded-lg py-3 text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                进入后台
              </button>
              {attempts > 0 && !pwError && (
                <p className="text-[#5a7a9a] text-xs text-center">
                  已失败 {attempts} / {MAX_ATTEMPTS} 次
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const countryStats = getCountryStats(visitors);
  const todayCount = getTodayCount(visitors);
  const uniqueIPs = getUniqueIPs(visitors);
  const topCountry = countryStats[0]?.country ?? "—";

  return (
    <div className="min-h-screen bg-[#070c1a] text-white">
      {/* Header */}
      <header className="bg-[#0d1529] border-b border-[#1e2d4a] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0a1628] text-[#4fc3f7]">
            <i className="ri-bar-chart-2-line text-lg"></i>
          </div>
          <div>
            <h1 className="text-white font-bold text-base leading-tight">访问统计后台</h1>
            <p className="text-[#5a7a9a] text-xs">星核动力科技</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[#5a7a9a] hover:text-[#4fc3f7] hover:bg-[#0a1628] text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            刷新
          </button>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[#5a7a9a] hover:text-red-400 hover:bg-[#1a0d0d] text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            清空数据
          </button>
          <button
            onClick={() => setAuthed(false)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0a1628] text-[#5a7a9a] hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap border border-[#1e2d4a]"
          >
            <i className="ri-logout-box-r-line"></i>
            退出
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="总访问次数" value={visitors.length} icon="ri-eye-line" sub="累计会话数" />
          <StatCard label="今日访问" value={todayCount} icon="ri-calendar-check-line" sub="今日新增" />
          <StatCard label="独立访客 IP" value={uniqueIPs} icon="ri-user-3-line" sub="唯一 IP 数量" />
          <StatCard label="访客最多来源" value={topCountry} icon="ri-global-line" sub={`共 ${countryStats.length} 个国家/地区`} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-[#0d1529] border border-[#1e2d4a] rounded-xl p-1 w-fit mb-6">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "overview"
                ? "bg-[#4fc3f7] text-[#070c1a]"
                : "text-[#5a7a9a] hover:text-white"
            }`}
          >
            <i className="ri-pie-chart-line mr-1.5"></i>
            国家分布
          </button>
          <button
            onClick={() => setActiveTab("list")}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "list"
                ? "bg-[#4fc3f7] text-[#070c1a]"
                : "text-[#5a7a9a] hover:text-white"
            }`}
          >
            <i className="ri-list-check mr-1.5"></i>
            访客明细
          </button>
          <button
            onClick={() => setActiveTab("content")}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "content"
                ? "bg-[#4fc3f7] text-[#070c1a]"
                : "text-[#5a7a9a] hover:text-white"
            }`}
          >
            <i className="ri-edit-2-line mr-1.5"></i>
            内容管理
          </button>
          <button
            onClick={() => setActiveTab("messages")}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === "messages"
                ? "bg-[#4fc3f7] text-[#070c1a]"
                : "text-[#5a7a9a] hover:text-white"
            }`}
          >
            <i className="ri-mail-open-line mr-1.5"></i>
            留言板
            {unreadMessages > 0 && (
              <span className="ml-2 inline-flex min-w-5 h-5 px-1 items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">
                {unreadMessages}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        {activeTab === "content" ? (
          <AdminErrorBoundary>
            <ContentEditor />
          </AdminErrorBoundary>
        ) : activeTab === "messages" ? (
          <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl overflow-hidden">
            <div className="p-6 border-b border-[#1e2d4a] flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <i className="ri-customer-service-2-line text-[#4fc3f7]"></i>
                  留言板查看
                </h2>
                <p className="text-[#5a7a9a] text-sm mt-1">共 {messages.length} 条留言，未读 {unreadMessages} 条</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {messages.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#1e2d4a] bg-[#0a1628] px-6 py-12 text-center">
                  <i className="ri-inbox-line text-3xl text-[#4fc3f7]"></i>
                  <p className="text-white text-sm font-medium mt-3">暂时还没有留言</p>
                  <p className="text-[#5a7a9a] text-xs mt-1">前台访客提交联系表单后会显示在这里。</p>
                </div>
              ) : (
                messages.map((item) => {
                  const busy = messageBusyId === item.id;
                  return (
                    <div key={item.id} className={`rounded-xl border p-4 ${item.isRead ? "bg-[#0a1628] border-[#1a2840]" : "bg-cyan-500/5 border-cyan-500/20"}`}>
                      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-white text-sm font-semibold">{item.name || "未填写姓名"}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] border ${item.isRead ? "text-[#8aaec8] border-[#1e2d4a]" : "text-cyan-300 border-cyan-500/30 bg-cyan-500/10"}`}>
                              {item.isRead ? "已阅" : "未读"}
                            </span>
                            {item.type && <span className="px-2 py-0.5 rounded-full text-[10px] text-amber-300 border border-amber-500/20 bg-amber-500/10">{item.type}</span>}
                          </div>
                          <p className="text-[#5a7a9a] text-xs">提交时间：{formatSubmissionTime(item.createdAt)}</p>
                          {item.readAt && <p className="text-[#5a7a9a] text-xs">已阅时间：{formatSubmissionTime(item.readAt)}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={busy || item.isRead}
                            onClick={() => handleMarkRead(item.id)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${busy || item.isRead ? "bg-[#1e2d4a] text-[#5a7a9a] cursor-not-allowed" : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 cursor-pointer hover:bg-emerald-500/20"}`}
                          >
                            {busy ? "处理中..." : item.isRead ? "已标记已阅" : "标记已阅"}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleDeleteMessage(item.id)}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${busy ? "bg-[#1e2d4a] text-[#5a7a9a] cursor-not-allowed" : "bg-red-500/15 text-red-400 border border-red-500/30 cursor-pointer hover:bg-red-500/20"}`}
                          >
                            删除
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-xs">
                        <div className="rounded-lg bg-[#071120] border border-[#1e2d4a] px-3 py-2">
                          <span className="text-[#5a7a9a] block mb-1">电话</span>
                          <span className="text-white break-all">{item.phone || "—"}</span>
                        </div>
                        <div className="rounded-lg bg-[#071120] border border-[#1e2d4a] px-3 py-2">
                          <span className="text-[#5a7a9a] block mb-1">邮箱</span>
                          <span className="text-white break-all">{item.email || "—"}</span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-[#071120] border border-[#1e2d4a] px-3 py-3">
                        <p className="text-[#5a7a9a] text-xs mb-2">留言内容</p>
                        <p className="text-white text-sm leading-6 whitespace-pre-wrap break-words">{item.message || "—"}</p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-xl overflow-hidden">
            {activeTab === "overview" ? (
              <div className="p-6">
                <h2 className="text-white font-semibold mb-6 flex items-center gap-2">
                  <i className="ri-global-line text-[#4fc3f7]"></i>
                  访客国家 / 地区统计
                </h2>
                <CountryChart data={countryStats} />
              </div>
            ) : (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-white font-semibold flex items-center gap-2">
                    <i className="ri-user-search-line text-[#4fc3f7]"></i>
                    访客记录明细
                  </h2>
                  <span className="text-[#5a7a9a] text-sm">共 {visitors.length} 条记录</span>
                </div>
                <VisitorTable visitors={visitors} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clear Confirm Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#0d1529] border border-[#1e2d4a] rounded-2xl p-8 max-w-sm w-full mx-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-900/30 border border-red-800 text-red-400 text-xl mx-auto mb-4">
              <i className="ri-error-warning-line"></i>
            </div>
            <h3 className="text-white font-bold text-center mb-2">确认清空数据？</h3>
            <p className="text-[#5a7a9a] text-sm text-center mb-6">
              此操作将删除所有访客记录，无法恢复。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 rounded-lg border border-[#1e2d4a] text-[#8aaec8] hover:text-white text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleClear}
                className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
